import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, DollarSign } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'compoundInterest_data';

export const CompoundInterestCalculator = () => {
  // State for inputs
  const [principal, setPrincipal] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).principal : 10000;
  });
  
  const [rate, setRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).rate : 8;
  });
  
  const [frequency, setFrequency] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).frequency : 'yearly';
  });
  
  const [years, setYears] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).years : 10;
  });

  // Save to localStorage whenever inputs change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      principal,
      rate,
      frequency,
      years
    }));
  }, [principal, rate, frequency, years]);

  // Frequency mapping
  const frequencyMap = {
    'yearly': { n: 1, label: 'Yearly' },
    'half-yearly': { n: 2, label: 'Half-Yearly' },
    'quarterly': { n: 4, label: 'Quarterly' },
    'monthly': { n: 12, label: 'Monthly' },
    'daily': { n: 365, label: 'Daily' }
  };

  // Calculate compound interest
  const calculateCompoundInterest = () => {
    const p = parseFloat(principal) || 0;
    const r = parseFloat(rate) / 100 || 0;
    const n = frequencyMap[frequency].n;
    const t = parseFloat(years) || 0;

    // A = P(1 + r/n)^(nt)
    const amount = p * Math.pow(1 + r / n, n * t);
    const interest = amount - p;
    const effectiveRate = (Math.pow(1 + r / n, n) - 1) * 100;

    return {
      finalAmount: amount,
      totalInterest: interest,
      effectiveRate: effectiveRate,
      principal: p
    };
  };

  // Generate year-wise data
  const generateYearWiseData = () => {
    const p = parseFloat(principal) || 0;
    const r = parseFloat(rate) / 100 || 0;
    const n = frequencyMap[frequency].n;
    const t = parseFloat(years) || 0;

    const data = [];
    for (let year = 0; year <= t; year++) {
      const amount = p * Math.pow(1 + r / n, n * year);
      const interest = amount - p;
      
      data.push({
        year: year,
        amount: Math.round(amount),
        principal: p,
        interest: Math.round(interest)
      });
    }
    return data;
  };

  // Compare compounding frequencies
  const compareFrequencies = () => {
    const p = parseFloat(principal) || 0;
    const r = parseFloat(rate) / 100 || 0;
    const t = parseFloat(years) || 0;

    return Object.keys(frequencyMap).map(freq => {
      const n = frequencyMap[freq].n;
      const amount = p * Math.pow(1 + r / n, n * t);
      return {
        name: frequencyMap[freq].label,
        value: Math.round(amount)
      };
    });
  };

  // Simple vs Compound comparison data
  const getSimpleVsCompoundData = () => {
    const p = parseFloat(principal) || 0;
    const r = parseFloat(rate) / 100 || 0;
    const n = frequencyMap[frequency].n;
    const t = parseFloat(years) || 0;

    const data = [];
    for (let year = 0; year <= t; year++) {
      const compound = p * Math.pow(1 + r / n, n * year);
      const simple = p * (1 + r * year);
      
      data.push({
        year: year,
        compound: Math.round(compound),
        simple: Math.round(simple)
      });
    }
    return data;
  };

  const results = calculateCompoundInterest();
  const yearWiseData = generateYearWiseData();
  const frequencyComparison = compareFrequencies();
  const simpleVsCompound = getSimpleVsCompoundData();

  // Chart colors
  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Navigation */}
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Compound Interest Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          {/* Horizontal Scrollable Tabs */}
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab 1: Calculator Overview & Results */}
          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            {/* Introduction */}
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Compound Interest?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Compound interest is the eighth wonder of the world. Unlike simple interest which only earns on the principal, 
                  compound interest earns on both the principal and accumulated interest. This calculator helps you understand 
                  how your money can grow exponentially over time with the power of compounding.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Input Section */}
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your investment details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="principal" className="text-sm font-medium">
                      Principal Amount ($)
                    </Label>
                    <Input
                      id="principal"
                      type="number"
                      value={principal}
                      onChange={(e) => setPrincipal(e.target.value)}
                      placeholder="10000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rate" className="text-sm font-medium">
                      Annual Interest Rate (%)
                    </Label>
                    <Input
                      id="rate"
                      type="number"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      placeholder="8"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="frequency" className="text-sm font-medium">
                      Compounding Frequency
                    </Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger id="frequency" className="text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(frequencyMap).map(key => (
                          <SelectItem key={key} value={key}>
                            {frequencyMap[key].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="years" className="text-sm font-medium">
                      Time Period (Years)
                    </Label>
                    <Input
                      id="years"
                      type="number"
                      value={years}
                      onChange={(e) => setYears(e.target.value)}
                      placeholder="10"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-primary"
                    onClick={() => {
                      // Trigger recalculation by forcing a re-render
                      setPrincipal(principal);
                    }}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              {/* Results Section */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Calculation Results</CardTitle>
                    <CardDescription>Your investment growth summary</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--primary-light))] to-white border border-[hsl(var(--primary))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))] flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Final Maturity Value</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--primary))]">
                          {formatCurrency(results.finalAmount)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--accent-light))] to-white border border-[hsl(var(--accent))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest Earned</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--accent))]">
                          {formatCurrency(results.totalInterest)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--secondary-light))] to-white border border-[hsl(var(--secondary))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Effective Annual Rate</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--secondary))]">
                          {results.effectiveRate.toFixed(2)}%
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--purple-light))] to-white border border-[hsl(var(--purple))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--purple))] flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Principal Invested</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--purple))]">
                          {formatCurrency(results.principal)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Year-wise Growth Table */}
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Year-wise Growth Breakdown</CardTitle>
                    <CardDescription>Detailed view of your investment growth over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 font-semibold text-foreground">Year</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Principal</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Interest</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Total Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearWiseData.map((row, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                            >
                              <td className="py-3 px-4 font-medium">{row.year}</td>
                              <td className="py-3 px-4 text-right text-muted-foreground">{formatCurrency(row.principal)}</td>
                              <td className="py-3 px-4 text-right text-[hsl(var(--accent))]">{formatCurrency(row.interest)}</td>
                              <td className="py-3 px-4 text-right font-semibold text-[hsl(var(--primary))]">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Visual Analysis (Charts) */}
          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Investment Growth Over Time</CardTitle>
                <CardDescription className="text-base">
                  Visualize how your investment grows year by year with compound interest
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={yearWiseData}>
                      <defs>
                        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="hsl(var(--primary))" 
                        fill="url(#colorAmount)"
                        strokeWidth={3}
                        name="Total Amount"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Principal vs Interest Contribution</CardTitle>
                  <CardDescription>
                    See how much of your final amount comes from principal vs earned interest
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={yearWiseData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="year" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="principal" stackId="a" fill="hsl(var(--chart-1))" name="Principal" />
                        <Bar dataKey="interest" stackId="a" fill="hsl(var(--chart-2))" name="Interest" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Compounding Frequency Impact</CardTitle>
                  <CardDescription>
                    Compare how different compounding frequencies affect your final returns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={frequencyComparison} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          type="number"
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <YAxis 
                          type="category"
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          width={100}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="value" fill="hsl(var(--chart-3))" name="Final Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Simple Interest vs Compound Interest</CardTitle>
                <CardDescription className="text-base">
                  This chart dramatically shows the power of compounding - how compound interest 
                  accelerates wealth growth compared to simple interest over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={simpleVsCompound}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="compound" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={3}
                        name="Compound Interest"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="simple" 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Simple Interest"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Explanation, Risks & Insights */}
          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Results</CardTitle>
                <CardDescription className="text-base">
                  A comprehensive breakdown of what the numbers mean for your financial future
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--primary))]">
                    How Compound Interest Works
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Compound interest is calculated using the formula: <span className="font-mono bg-muted px-2 py-1 rounded">A = P(1 + r/n)^(nt)</span>
                  </p>
                  <ul className="space-y-2 text-base text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-[hsl(var(--primary))] font-semibold">•</span>
                      <span><strong>P (Principal):</strong> Your initial investment of {formatCurrency(results.principal)}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[hsl(var(--primary))] font-semibold">•</span>
                      <span><strong>r (Rate):</strong> Annual interest rate of {rate}%</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[hsl(var(--primary))] font-semibold">•</span>
                      <span><strong>n (Frequency):</strong> Interest compounds {frequencyMap[frequency].n} times per year</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[hsl(var(--primary))] font-semibold">•</span>
                      <span><strong>t (Time):</strong> Investment period of {years} years</span>
                    </li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--accent))]">
                    Where Your Profits Come From
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your investment of {formatCurrency(results.principal)} will grow to {formatCurrency(results.finalAmount)} 
                    over {years} years. Here's the breakdown:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[hsl(var(--primary-light))] border border-[hsl(var(--primary))]/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Your Principal</p>
                      <p className="text-2xl font-bold text-[hsl(var(--primary))]">
                        {formatCurrency(results.principal)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        The money you invested
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--accent-light))] border border-[hsl(var(--accent))]/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Interest Earned</p>
                      <p className="text-2xl font-bold text-[hsl(var(--accent))]">
                        {formatCurrency(results.totalInterest)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Profit from compound growth
                      </p>
                    </div>
                  </div>
                  <p className="text-base text-muted-foreground leading-relaxed mt-4">
                    That's a <strong className="text-[hsl(var(--accent))]">
                      {((results.totalInterest / results.principal) * 100).toFixed(1)}%
                    </strong> return on your initial investment!
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--secondary))]">
                    How Time, Rate, and Frequency Affect Your Returns
                  </h3>
                  <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">⏰ Time is Your Best Friend</h4>
                      <p>
                        The longer your money compounds, the more dramatic the growth. Notice in the charts how the 
                        growth curve becomes steeper in later years - that's the exponential power of compounding at work.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📈 Interest Rate Impact</h4>
                      <p>
                        Even small differences in interest rates can lead to significant changes in final returns over time. 
                        A 1% increase in rate on a {years}-year investment can mean thousands of dollars in additional earnings.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">🔄 Compounding Frequency</h4>
                      <p>
                        More frequent compounding means your interest starts earning interest sooner. The difference 
                        between yearly and monthly compounding on your investment would be{' '}
                        {formatCurrency(
                          frequencyComparison.find(f => f.name === 'Monthly').value - 
                          frequencyComparison.find(f => f.name === 'Yearly').value
                        )}.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--destructive))]">
                    Important Risks and Assumptions
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Inflation Impact:</strong> This calculator doesn't account 
                        for inflation. Your {formatCurrency(results.finalAmount)} will have less purchasing power in 
                        the future due to inflation (typically 2-3% annually).
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Rate Assumption:</strong> This assumes a fixed interest 
                        rate of {rate}% throughout the entire period. Real investments often have variable rates.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Market Risk:</strong> Investments in stocks, mutual funds, 
                        or other securities carry risk. Past performance doesn't guarantee future results.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Tax Considerations:</strong> Investment returns may be 
                        subject to taxes, which would reduce your actual take-home returns.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">No Additional Contributions:</strong> This calculation 
                        assumes no additional deposits. Regular contributions would significantly increase returns.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--purple))]">
                    Practical Suggestions for Your Investment Strategy
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-[hsl(var(--accent-light))] border-l-4 border-[hsl(var(--accent))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Start Early</h4>
                      <p className="text-muted-foreground">
                        The most powerful factor in compound interest is time. Starting even 5 years earlier can 
                        dramatically increase your final returns.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--primary-light))] border-l-4 border-[hsl(var(--primary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Maximize Your Rate</h4>
                      <p className="text-muted-foreground">
                        Shop around for the best interest rates on savings accounts, bonds, or investment returns. 
                        Even a 0.5% difference matters over time.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary-light))] border-l-4 border-[hsl(var(--secondary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Choose Frequent Compounding</h4>
                      <p className="text-muted-foreground">
                        When comparing investment options, prefer those with more frequent compounding (monthly or daily 
                        vs yearly) as they generate higher effective returns.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--purple-light))] border-l-4 border-[hsl(var(--purple))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Stay Invested Long-Term</h4>
                      <p className="text-muted-foreground">
                        The exponential growth curve shows returns accelerate in later years. Avoid withdrawing early 
                        to maximize compound growth.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CompoundInterestCalculator;
